import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Textarea } from '@/common/ui/textarea'
import { Skeleton } from '@/common/ui/skeleton'
import { Switch } from '@/common/ui/switch'
import { Label } from '@/common/ui/label'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import useQueryGetAutoMessageById from '../../api/hooks/useQueryGetAutoMessageById'
import useMutationUpdateAutoMessage from '../../api/hooks/useMutationUpdateAutoMessage'
import { getConditionLabel } from '../../utils/conditionLabels'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

const editAutoMessageFormSchema = z.object({
	message: z.string().min(1, 'กรุณากรอกข้อความ').max(1000, 'ข้อความยาวเกินไป'),
	isActive: z.boolean(),
})

type EditAutoMessageFormValues = z.infer<typeof editAutoMessageFormSchema>

const EditAutoMessagePage = () => {
	const navigate = useNavigate()
	const { id } = useParams<{ id: string }>()
	const { data: autoMessage, isLoading } = useQueryGetAutoMessageById(id || '')
	const updateAutoMessageMutation = useMutationUpdateAutoMessage()

	const form = useForm<EditAutoMessageFormValues>({
		resolver: zodResolver(editAutoMessageFormSchema),
		defaultValues: {
			message: '',
			isActive: false,
		},
	})

	useEffect(() => {
		if (autoMessage) {
			form.reset({
				message: autoMessage.message,
				isActive: autoMessage.isActive,
			})
		}
	}, [autoMessage, form])

	const onSubmit = async (values: EditAutoMessageFormValues) => {
		if (!id) return

		try {
			await updateAutoMessageMutation.mutateAsync({
				id,
				data: {
					message: values.message,
					isActive: values.isActive,
				},
			})

			toast.success('อัปเดตข้อความอัตโนมัติสำเร็จ')
			navigate('/operator/messaging/auto')
		} catch {
			toast.error('เกิดข้อผิดพลาดในการอัปเดตข้อความอัตโนมัติ')
		}
	}

	if (isLoading) {
		return (
			<PageContainer>
				<div className="space-y-6">
					<Skeleton className="h-10 w-64" />
					<Card>
						<CardHeader>
							<Skeleton className="h-6 w-32" />
						</CardHeader>
						<CardContent className="space-y-4">
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-32 w-full" />
							<Skeleton className="h-10 w-full" />
						</CardContent>
					</Card>
				</div>
			</PageContainer>
		)
	}

	if (!autoMessage) {
		return (
			<PageContainer>
				<div className="text-center">
					<h2 className="text-2xl font-bold">ไม่พบข้อมูล</h2>
					<Button
						className="mt-4"
						onClick={() => navigate('/operator/messaging/auto')}
					>
						กลับไปหน้ารายการ
					</Button>
				</div>
			</PageContainer>
		)
	}

	const characterCount = form.watch('message')?.length || 0

	return (
		<PageContainer>
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold">แก้ไขข้อความอัตโนมัติ</h1>
					<p className="mt-2 text-muted-foreground">
						แก้ไขข้อความและตั้งค่าข้อความอัตโนมัติ
					</p>
				</div>

				<Card>
					<CardHeader>
						<CardTitle>ตั้งค่าการส่งข้อความอัตโนมัติ</CardTitle>
					</CardHeader>
					<CardContent>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit, showFormValidationToast)}
								className="space-y-6"
							>
								{/* Show condition (read-only) */}
								<div className="space-y-2">
									<Label>เงื่อนไข</Label>
									<div className="rounded-lg border bg-muted p-3">
										{getConditionLabel(autoMessage.autoMessageGroupCondition)}
									</div>
									<p className="text-sm text-muted-foreground">
										ไม่สามารถเปลี่ยนเงื่อนไขได้หลังจากสร้างแล้ว
									</p>
								</div>

								{/* Show condition values (read-only) */}
								{(autoMessage.minimum != null ||
									autoMessage.maximum != null) && (
									<div className="space-y-2">
										<Label>ค่าเงื่อนไข</Label>
										<div className="rounded-lg border bg-muted p-3">
											{autoMessage.minimum != null && (
												<span>ค่าต่ำสุด: {autoMessage.minimum}</span>
											)}
											{autoMessage.maximum != null && (
												<span className="ml-4">
													ค่าสูงสุด: {autoMessage.maximum}
												</span>
											)}
										</div>
									</div>
								)}

								{/* Active status */}
								<FormField
									control={form.control}
									name="isActive"
									render={({ field }) => (
										<FormItem className="flex items-center justify-between rounded-lg border p-4">
											<div className="space-y-0.5">
												<FormLabel className="text-base">
													สถานะการใช้งาน
												</FormLabel>
												<FormDescription>
													เปิดหรือปิดการทำงานของข้อความอัตโนมัตินี้
												</FormDescription>
											</div>
											<FormControl>
												<Switch
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
										</FormItem>
									)}
								/>

								{/* Message */}
								<FormField
									control={form.control}
									name="message"
									render={({ field }) => (
										<FormItem>
											<FormLabel>ข้อความ</FormLabel>
											<FormControl>
												<Textarea
													placeholder="กรอกข้อความที่ต้องการส่ง"
													className="min-h-[120px]"
													{...field}
												/>
											</FormControl>
											<FormDescription>
												{characterCount} / 1000 ตัวอักษร
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								{/* Submit buttons */}
								<div className="flex gap-3">
									<Button
										type="button"
										variant="outline"
										onClick={() => navigate('/operator/messaging/auto')}
									>
										ยกเลิก
									</Button>
									<Button
										type="submit"
										disabled={updateAutoMessageMutation.isPending}
									>
										{updateAutoMessageMutation.isPending
											? 'กำลังบันทึก...'
											: 'บันทึกการเปลี่ยนแปลง'}
									</Button>
								</div>
							</form>
						</Form>
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	)
}

const GuardedEditAutoMessagePage = withUserPermission(EditAutoMessagePage, {
	permissionType: PermissionType.Write,
	permissionKey: [PermissionKeys.MANAGE_SMS],
})

export { GuardedEditAutoMessagePage as EditAutoMessagePage }
